import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { RefObject, useRef } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";

import shipCore from "../../core/ship.core";
import { useTranslation } from "../../core/translation.core";
import CloseIcon from "../../icons/iconClose.comp";
import SolidWeakBtn from "../form/solidWeakBtn.comp";

export default function MultipleShipOptionsSelector({
  methods,
  fieldName,
  buttonText,
  btnId,
}: {
  methods: UseFormReturn<any, any>;
  fieldName: string;
  buttonText?: string;
  btnId?: string;
}) {
  const { t, msg } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);

  function deleteShipOption(shipOptionId: string) {
    const shipOptionIds = methods.getValues(fieldName) || [];

    const set = new Set(shipOptionIds);
    set.delete(shipOptionId);
    methods.setValue(fieldName, Array.from(set));
  }

  return (
    <div className="flex flex-col gap-2">
      <AttachBadges methods={methods} fieldName={fieldName} handleDelete={deleteShipOption} />
      <button
        type="button"
        id={btnId}
        className="bg-her-blue px-4 py-2 rounded-md text-white w-fit dark:bg-her-dodgerblue"
        onClick={() => dialogRef.current?.showModal()}
      >
        {buttonText ?? t(msg.ship.selectShipOption)}
      </button>
      <ShipOptionSelectorDialog dialogRef={dialogRef} methods={methods} fieldName={fieldName} />
    </div>
  );
}

const AttachBadges = ({
  methods,
  fieldName,
  handleDelete,
}: {
  fieldName: string;
  methods: UseFormReturn<any, any>;
  handleDelete: (shipOptionId: string) => void;
}) => {
  const shipOptionIds = useWatch({ control: methods.control, name: fieldName }) || [];

  if (shipOptionIds.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {shipOptionIds.map((shipOptionId: string) => (
        <ShipOptionBadge key={shipOptionId} shipOptionId={shipOptionId} handleDelete={handleDelete} />
      ))}
    </div>
  );
};

function ShipOptionBadge({ shipOptionId, handleDelete }: { shipOptionId: string; handleDelete: (id: string) => void }) {
  const { isReady } = useRouter();

  const { data: shipSettings } = useQuery({
    queryKey: ["shipSettings"],
    queryFn: () => shipCore.getShipSettings(),
    enabled: isReady,
  });

  const shipOption = shipSettings?.flatMap((s) => s.options).find((o) => o.id === shipOptionId);

  if (!shipOption) return null;

  return (
    <div className="flex items-center px-5 py-2.5 text-sm font-medium text-center text-her-blue border border-her-blue rounded-lg focus:ring-4 focus:outline-none focus:ring-blue-300 dark:text-her-dodgerblue dark:border-her-dodgerblue">
      {shipOption.name}
      <button
        type="button"
        className="flex items-center justify-center w-4 h-4 ml-2 text-xs font-semibold text-blue-800 bg-blue-200 rounded-full hover:bg-red-400 hover:text-white dark:bg-her-backblue dark:text-white"
        onClick={(e) => {
          e.preventDefault();
          handleDelete(shipOptionId);
        }}
      >
        <CloseIcon />
      </button>
    </div>
  );
}

interface ShipOptionSelectorDialogProps {
  dialogRef: RefObject<HTMLDialogElement>;
  methods: UseFormReturn<any, any>;
  fieldName: string;
}

function ShipOptionSelectorDialog({ dialogRef, methods, fieldName }: ShipOptionSelectorDialogProps) {
  const { t, msg } = useTranslation();
  const { isReady } = useRouter();

  const { data: shipSettings, isPending } = useQuery({
    queryKey: ["shipSettings"],
    queryFn: () => shipCore.getShipSettings(),
    enabled: isReady,
  });

  const selectedShipOptionIds = useWatch({ control: methods.control, name: fieldName }) || [];

  function addShipOption(shipOptionId: string) {
    const shipOptionIds = methods.getValues(fieldName) || [];

    const set = new Set(shipOptionIds);
    set.add(shipOptionId);
    methods.setValue(fieldName, Array.from(set));
  }

  const shipOptions = shipSettings?.flatMap((s) => s.options) ?? [];
  const availableShipOptions = shipOptions.filter((o) => !selectedShipOptionIds.includes(o.id));

  return (
    <dialog
      id="add-multiple-ship-options-dialog"
      ref={dialogRef}
      className="dark:bg-her-blackOlive dark:text-white p-10 rounded-lg w-[80vw] max-w-[700px] fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
    >
      <h1 className="text-xl mb-4">{t(msg.ship.selectShipOption)}</h1>
      {isPending ? (
        <div>Loading...</div>
      ) : (
        <div id="select-ship-options-list" className="flex flex-col gap-2 max-h-[400px] overflow-y-auto">
          {availableShipOptions.length === 0 ? (
            <p className="text-gray-400 dark:text-gray-500">{t(msg.ship.none)}</p>
          ) : (
            availableShipOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                data-ship-option-id={option.id}
                data-ship-type={option.shipType}
                className="w-full text-left px-4 py-2 bg-admin-bg-blue hover:bg-admin-bg-blue-dark rounded dark:bg-her-backblue dark:hover:bg-her-blackOlive"
                onClick={() => addShipOption(option.id)}
              >
                {option.name}
              </button>
            ))
          )}
        </div>
      )}
      <div className="flex gap-4 mt-6">
        <SolidWeakBtn
          id="close-multiple-ship-options-dialog-btn"
          type="button"
          onClick={() => dialogRef.current?.close()}
          ariaLabel={t(msg.form.close)}
        >
          {t(msg.form.close)}
        </SolidWeakBtn>
      </div>
    </dialog>
  );
}
